import { SEND_BLOCK_UNTIL_ONLY_WITH_BLOCK } from '@/shared/action/disapproval-fields';

import type {
  CancelBody,
  DisapproveBody,
  ListDisapproveBody,
  UploadUpdate,
} from './credit-note.wire';

// Montagem dos corpos que o legado faz na tela, fora dela para o contrato ser testável.

// U20-4: uma chave só, a de `shared/action` (fiel ao legado: `false` manda `blockUntil` mesmo sem bloqueio).
export { SEND_BLOCK_UNTIL_ONLY_WITH_BLOCK };

type InsertBlock = 'Person' | 'Operation' | null;

function withBlockUntil<T extends { insertBlock: InsertBlock; blockUntil?: string | null | undefined }>(body: T): T {
  if (!SEND_BLOCK_UNTIL_ONLY_WITH_BLOCK || body.insertBlock !== null) return body;
  const { blockUntil: _omitted, ...rest } = body;
  return rest as T;
}

export interface DisapproveFormValues {
  message?: string | null;
  insertBlock: InsertBlock;
  blockUntil?: string | null;
  complianceChecklist?: string[] | null;
  rejectionReasonCode?: string[] | null;
}

/** Detalhe: `complianceChecklist` vira `attributeTypes` (RejectFormContainer.tsx:52-60). */
export function toDisapproveBody(values: DisapproveFormValues): DisapproveBody {
  return withBlockUntil<DisapproveBody>({
    message: values.message,
    insertBlock: values.insertBlock,
    blockUntil: values.blockUntil,
    attributeTypes: values.complianceChecklist,
    rejectionReasonCode: values.rejectionReasonCode,
  });
}

export interface ListDisapproveFormValues {
  rejectionReasonCode?: string[] | null;
  message: string;
  insertBlock: 'not' | 'Person' | 'Operation';
  blockUntil: string;
}

/** Lista: espalha o formulário e troca `insertBlock: 'not'` por `null` (CreditNoteRejectComplianceContainer.tsx:38-42). */
export function toListDisapproveBody(values: ListDisapproveFormValues): ListDisapproveBody {
  return withBlockUntil<ListDisapproveBody>({ ...values, insertBlock: values.insertBlock === 'not' ? null : values.insertBlock });
}

/** Default da lista: `hoje T00:00:00`, sem fuso (CreditNoteRejectComplianceSchema.ts:22). */
export function listBlockUntilDefault(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T00:00:00`;
}

export interface CancelFormValues {
  message: string;
  insertBlock: InsertBlock;
  blockUntil?: string | null;
  rejectionReasonCode: string[];
  complianceChecklist?: string[] | null;
}

/** CancelFormContainer.tsx:54-60: `blockUntil` default `null`; `complianceChecklist` vira `attributeTypes`. */
export function toCancelBody(values: CancelFormValues): CancelBody {
  return withBlockUntil<CancelBody>({
    message: values.message,
    insertBlock: values.insertBlock,
    blockUntil: values.blockUntil,
    rejectionReasonCode: values.rejectionReasonCode,
    attributeTypes: values.complianceChecklist,
  });
}

/**
 * Mensagem da reprovação de assinatura: "rótulos, Outros motivos: texto"
 * (`formatMessageReasonsDisapprove`, helpers/formats/ArrayFormats.ts:67-81).
 */
export function formatRejectionMessage(labels: readonly string[], otherLabel: string, complement: string): string {
  const joined = labels.filter((label) => label !== otherLabel).join(', ');
  if (labels[0] === otherLabel) return `${otherLabel}: ${complement}`;
  if (labels.includes(otherLabel)) return `${joined}. ${otherLabel}: ${complement}`;
  return joined;
}

export interface ReleaseToPaymentFormValues {
  action: 'Release' | 'Cancel' | 'Sign';
  [field: string]: unknown;
}

/** creditNote.ts:626-635: tira os `*Display` e só manda `Message` quando a ação é `Cancel`. */
export function toReleaseToPaymentBody(values: ReleaseToPaymentFormValues) {
  const { action, rejectionReasonCodeDisplay: _a, actionDisplay: _b, complianceChecklistDisplay: _c, ...rest } = values;
  return { action, Message: action === 'Cancel' ? rest : undefined };
}

/** `mapUploadModelData`: `toDataModel` mantém só as chaves do `UploadUpdateModel` presentes no item. */
export function toUploadUpdates(uploads: readonly Record<string, unknown>[]): UploadUpdate[] {
  const keys = ['id', 'fileType', 'fileName', 'displayName'] as const;
  return uploads.map((upload) => {
    const out: Record<string, unknown> = {};
    for (const key of keys) if (key in upload) out[key] = upload[key];
    return out as UploadUpdate;
  });
}

/** `isDateInThePast` (helpers/methods/IsDateInThePast.ts:8-17): compara dias no fuso local. */
export function isStartDateInPast(startDate: string | null | undefined, now: Date): boolean {
  const target = new Date(startDate ?? '');
  target.setHours(0, 0, 0, 0);
  const today = new Date(now.getTime());
  today.setHours(0, 0, 0, 0);
  return today.getTime() > target.getTime();
}

/**
 * `updateCreditNoteByIdAsync` (creditNoteActions.tsx:520-526): o corpo são os valores do formulário, sem
 * `toDataModel`, e sem plano customizado `paymentScheduleItems` vai vazio.
 */
export function prepareCreditNoteUpdate<T extends { customPaymentPlan?: unknown; paymentScheduleItems?: unknown }>(values: T): T {
  return { ...values, ...(values.customPaymentPlan ? {} : { paymentScheduleItems: [] }) };
}

/**
 * "Atualizar proposta" (creditNoteActions.tsx:535-540 + 520-526): início vira a meia-noite local de hoje em
 * ISO e o resto segue `prepareCreditNoteUpdate`.
 */
export function prepareStartDateUpdate<T extends { amortization?: unknown; customPaymentPlan?: unknown; paymentScheduleItems?: unknown }>(
  values: T,
  now: Date,
): T {
  const midnight = new Date(now.getTime());
  midnight.setHours(0, 0, 0, 0);
  const amortization = { ...(values.amortization as Record<string, unknown> | undefined), startDate: midnight.toISOString() };
  return prepareCreditNoteUpdate({ ...values, amortization });
}

/** `generateBatchAssignment` na lista (creditNoteListActions.tsx:179-185): `assignmentCalculation: null` sai. */
export function toBatchAssignmentBody(creditNoteIds: readonly string[], values: Record<string, unknown> = {}) {
  const body: Record<string, unknown> = { creditNoteIds, ...values };
  if (body['assignmentCalculation'] === null) delete body['assignmentCalculation'];
  return body;
}
